/**
 * packagePreviewApi — the Risk Panel disclosure package generator.
 *
 *   GET  /v3/risk/package-preview/catalog   ownership types, products and the
 *                                           elections each product offers
 *   POST /v3/risk/package-preview           scenario -> the package the signing
 *                                           path would build, from an in-memory
 *                                           test cart that is never saved
 *
 * Server: apps/PCBRetailApi/Functions/V3RiskPackagePreviewFunction.cs +
 * Services/DisclosurePackagePreviewService.cs. Bearer-only, like every other
 * call this panel makes.
 */
import { buildHeaders, getBaseUrl, readJsonOrThrow } from '@/services/riskSettingsApi';

export interface CatalogOwnershipType {
  readonly customerType: string;
  readonly displayName: string;
  readonly status: string;
  readonly phase: string | null;
  readonly personalOnly: boolean;
  readonly businessOnly: boolean;
  readonly inPhase1Pilot: boolean;
}

export interface CatalogDriver {
  readonly featureCode: string;
  readonly state: string;
  readonly offered: boolean;
}

export interface CatalogProduct {
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly acctType: string;
  readonly minimumDeposit: number | null;
  readonly requiresGuardian: boolean;
  readonly allowJoints: boolean;
  readonly maxJointOwners: number;
  readonly allowBeneficiaries: boolean;
  readonly maxBeneficiaries: number;
  readonly customerTypes: ReadonlyArray<string>;
  /** Derived (catalog account type or the products.interestBearingCodes setting) — not an election. */
  readonly interestBearing: boolean;
  readonly checks: CatalogDriver | null;
  readonly overdraft: CatalogDriver | null;
}

export interface PreviewCatalog {
  readonly ownershipTypes: ReadonlyArray<CatalogOwnershipType>;
  readonly products: ReadonlyArray<CatalogProduct>;
  readonly branches: ReadonlyArray<{ code: string; name: string }>;
  readonly odLimitTiers: ReadonlyArray<number>;
  readonly limits: { maxPeople: number; maxProducts: number; maxBeneficiaries: number };
}

export type PersonRole = 'primary' | 'joint' | 'custodian';
export type OverdraftChoice = 'none' | 'optOut' | 'optIn';
export type VersionSource = 'active' | 'includeDrafts';

export interface ScenarioPerson {
  role: PersonRole;
  minor: boolean;
}

export interface ScenarioBeneficiary {
  role: 'primary' | 'contingent';
  minor: boolean;
  percentage?: number | null;
}

export interface ScenarioProduct {
  code: string;
  /** Indexes into people; the first is the primary owner. Empty = everyone. */
  ownerIndexes: number[];
  orderChecks: boolean;
  overdraft: OverdraftChoice;
  odLimit: number;
  beneficiaries: ScenarioBeneficiary[];
}

export interface Scenario {
  ownershipType: string;
  stampOwnershipOnCart: boolean;
  branchCode: string;
  people: ScenarioPerson[];
  products: ScenarioProduct[];
  versionSource: VersionSource;
  compilePdfs: boolean;
}

export interface PreviewDocument {
  readonly order: number;
  readonly instanceId: string;
  readonly templateCode: string;
  readonly bodyTemplateCode: string | null;
  readonly title: string;
  readonly accountId: string | null;
  readonly versionNumber: string;
  readonly versionId: number;
  readonly versionStatus: string | null;
  readonly required: boolean;
  readonly requiresSignature: boolean;
  readonly signerRoles: ReadonlyArray<string>;
  readonly source: string | null;
  readonly pageCount: number;
  readonly pageCountMeasured: boolean;
  readonly fieldCount: number;
  readonly pdfBytes: number;
  readonly contentHash: string;
  readonly tokenWarnings: ReadonlyArray<string>;
  readonly reasons: ReadonlyArray<{ stage: string; reason: string }>;
  readonly pdfBase64: string;
}

export interface PreviewRun {
  readonly success: boolean;
  readonly runId: string;
  readonly generatedAtUtc: string;
  readonly elapsedMs: number;
  readonly versionSource: VersionSource;
  readonly compilePdfs: boolean;
  readonly entityType: string | null;
  readonly productCodes: ReadonlyArray<string>;
  readonly productCategories: ReadonlyArray<string>;
  readonly people: ReadonlyArray<{ index: number; name: string; role: string; minor: boolean; customerType: string }>;
  readonly scenarioNotes: ReadonlyArray<string>;
  readonly documents: ReadonlyArray<PreviewDocument>;
  readonly dropped: ReadonlyArray<{ templateCode: string; title: string; stage: string; reason: string }>;
  readonly notRendered: ReadonlyArray<{ templateCode: string; title: string }>;
  readonly resolverNotes: ReadonlyArray<string>;
  readonly errors: ReadonlyArray<string>;
  readonly warnings: ReadonlyArray<string>;
  readonly combinedPdfBase64: string | null;
  readonly caveat: string;
  readonly error?: string;
}

export async function loadPreviewCatalog(): Promise<PreviewCatalog> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/package-preview/catalog`, {
    method: 'GET',
    headers: await buildHeaders(),
  });
  const body = await readJsonOrThrow<{ success: boolean; catalog?: PreviewCatalog; error?: string }>(resp);
  if (!body.catalog) throw new Error('The server returned no catalog.');
  return body.catalog;
}

export async function runPackagePreview(scenario: Scenario, signal?: AbortSignal): Promise<PreviewRun> {
  const resp = await fetch(`${getBaseUrl()}/v3/risk/package-preview`, {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify(scenario),
    ...(signal ? { signal } : {}),
  });
  return readJsonOrThrow<PreviewRun>(resp);
}

/** base64 PDF -> object URL for a sandboxed iframe or a download. Caller revokes. */
export function pdfObjectUrl(base64: string): string {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}
